import { callAPI } from "../../helper/apiUtils";
import { $crud } from "../../helper/CrudFactory";

/**
 * The Management-Fazit the user is creating, kept outside the Cockpit page:
 * the server builds it in the background (POST /email/daily-briefing/run with
 * `background`), and the user may go anywhere in the app meanwhile. The
 * outcome is a toast wherever they are; a Cockpit that is open reloads its
 * reports, one opened later shows the run's progress again. The run id is
 * kept in localStorage, so a reload of the page picks the run up too.
 *
 * job: { runId, buildingId, progress: { phase, itemCount, value, live, startedAt } }
 */

const STORE_KEY = "cockpitReportRun";
const POLL_MS = 2000;
const POLL_LIMIT_MS = 15 * 60 * 1000;

let job = null;
const listeners = new Set();
const finishedListeners = new Set();

function emit() {
  listeners.forEach((listener) => listener());
}

function remember(value) {
  try {
    if (value) window.localStorage.setItem(STORE_KEY, JSON.stringify(value));
    else window.localStorage.removeItem(STORE_KEY);
  } catch (_) {
    /* private mode or storage blocked: the run just isn't resumed after a reload */
  }
}

function remembered() {
  try {
    const value = JSON.parse(window.localStorage.getItem(STORE_KEY) || "null");
    return value && value.runId ? value : null;
  } catch (_) {
    return null;
  }
}

// The request can run for minutes before the server reports a phase. Move
// through the same stages as the admin panel until that update arrives.
// Called every 400 ms by a Cockpit that shows the run.
export function tickReportJob() {
  const prev = job && job.progress;
  if (!prev || prev.live) return;
  const elapsed = (Date.now() - (prev.startedAt || Date.now())) / 1000;
  const value = Math.min(90, Math.round(8 + 82 * (1 - Math.exp(-elapsed / 35))));
  let phase = "queued";
  if (value >= 78) phase = "priorities";
  else if (value >= 34) phase = "summarizing";
  else if (value >= 20) phase = "reading_stored_text";
  else if (value >= 12) phase = "collecting";
  if (prev.phase === phase && prev.value === value) return;
  job = { ...job, progress: { ...prev, phase, value } };
  emit();
}

function begin(next) {
  job = next;
  emit();
}

function end() {
  job = null;
  remember(null);
  emit();
}

export function subscribeReportJob(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getReportJob() {
  return job;
}

/** `listener({ buildingId })` after a report was created. */
export function onReportJobFinished(listener) {
  finishedListeners.add(listener);
  return () => finishedListeners.delete(listener);
}

export function reportCreateError(data, strings) {
  const reason = data && data.reason;
  if (reason === "no_connected_account") {
    return strings.cockpit_report_needs_mailbox || "Verbinde ein E-Mail-Konto, damit Kjuup einen Tagesbericht erstellen kann.";
  }
  if (reason === "no_room_activity") {
    return strings.cockpit_report_no_activity || "In diesem Projekt gab es in den letzten 24 Stunden keine neuen Nachrichten oder E-Mails.";
  }
  if (reason === "building_not_found_or_not_member") {
    return strings.cockpit_report_room_unavailable || "Dieses Projekt ist nicht verfügbar.";
  }
  return strings.cockpit_report_create_failed || "Der Tagesbericht konnte nicht erstellt werden.";
}

// The run's terminal state, or null when the run is gone (deleted, another
// user's after a logout) and there is nothing to report.
async function follow(runId) {
  const deadline = Date.now() + POLL_LIMIT_MS;
  while (Date.now() < deadline) {
    const res = await callAPI(`/email/daily-briefing/run/${encodeURIComponent(runId)}`, {}, "GET");
    if (!job || job.runId !== runId) return null;
    if (res && (res.status === 404 || res.status === 401)) return null;
    const data = (res && res.data) || {};
    const run = data.run;
    if (run) {
      job = {
        ...job,
        progress: { phase: run.phase || "queued", itemCount: Number(data.itemCount) || 0, live: true, startedAt: Date.now() },
      };
      emit();
      if (run.status === "done" || run.status === "failed" || run.status === "skipped") return run;
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
  }
  return { status: "failed", error: "timeout" };
}

async function settle(runId, buildingId, strings) {
  const run = await follow(runId);
  if (!job || job.runId !== runId) return;
  end();
  if (!run) return;
  if (run.status !== "done") {
    const outcome = { reason: run.error, skipped: run.status === "skipped" };
    $crud.notify({ type: "error", message: reportCreateError(outcome, strings) });
    return;
  }
  $crud.notify({ type: "success", message: strings.cockpit_report_created || "Tagesbericht erstellt" });
  finishedListeners.forEach((listener) => listener({ buildingId }));
}

/** Starts a report (one room with `buildingId`, else all projects). One at a time. */
export async function startReportJob(buildingId, strings) {
  if (job) return;
  const room = buildingId || null;
  begin({ runId: null, buildingId: room, progress: { phase: "queued", itemCount: 0, startedAt: Date.now(), value: 8 } });
  let data = {};
  try {
    const body = { force: true, background: true };
    if (room) body.buildingId = room;
    const res = await callAPI("/email/daily-briefing/run", {}, "POST", body);
    data = (res && res.data) || {};
  } catch (e) {
    end();
    $crud.notify({ type: "error", message: (e && e.message) || strings.something_went_wrong || "Something went wrong" });
    return;
  }
  if (!data.status || !data.runId) {
    end();
    // Without `background` support the server answers with the finished run.
    if (!data.status || data.skipped || data.persistError) {
      $crud.notify({ type: "error", message: reportCreateError(data, strings) });
      return;
    }
    $crud.notify({ type: "success", message: strings.cockpit_report_created || "Tagesbericht erstellt" });
    finishedListeners.forEach((listener) => listener({ buildingId: room }));
    return;
  }
  job = { ...job, runId: data.runId };
  remember({ runId: data.runId, buildingId: room, startedAt: Date.now() });
  await settle(data.runId, room, strings);
}

/** After a reload: follows the run started before it, if there is one. */
export function resumeReportJob(strings) {
  if (job) return;
  const saved = remembered();
  if (!saved) return;
  if (Date.now() - (Number(saved.startedAt) || 0) > POLL_LIMIT_MS) {
    remember(null);
    return;
  }
  begin({
    runId: saved.runId,
    buildingId: saved.buildingId || null,
    progress: { phase: "queued", itemCount: 0, startedAt: Number(saved.startedAt) || Date.now(), value: 8 },
  });
  settle(saved.runId, saved.buildingId || null, strings);
}
